<template lang="pug">
.event-date-select
  b-dropdown(ref="dates")
    a.button.is-full-width(slot="trigger")
      b-icon.has-text-grey-lighter(
        :icon="mode === 'date' ? 'calendar' : 'calendar-multiple'"
      )
      span.date-label
        span(v-if="mode === 'date'")
          span(v-if="date") {{ date | date('MMMM D, YYYY') }}
          span.has-text-grey-light(v-else) Set date
        span.date-range-label(v-else-if="mode === 'range'")
          span(v-if="range.from") {{ range.from | date('MMMM D, YYYY') }}
          span.has-text-grey-light(v-else) Start date
          span.separator
            b-icon(icon="chevron-right")
          span(v-if="range.to") {{ range.to | date('MMMM D, YYYY') }}
          span.has-text-grey-light(v-else) End date
        span(v-else-if="mode === 'dates'")
          span(v-if="dates.length > 0") {{ datesLabel }}
          span.has-text-grey-light(v-else) Set dates
    b-dropdown-item(custom)
      .calendar-controls
        div
          .buttons
            a.button(@click="nav('prev', 2)")
              b-icon(icon="chevron-double-left")
            a.button(@click="nav('prev')")
              b-icon(icon="chevron-left")
        div
          b-field
            b-radio-button(
              v-model="mode"
              native-value="date"
              @input="reset"
            ) Single Date
            b-radio-button(
              v-model="mode"
              native-value="range"
              @input="reset"
            ) Date Range
            b-radio-button(
              v-model="mode"
              native-value="dates"
              @input="reset"
            ) Multiple Dates
        div
          .buttons
            a.button(@click="nav('next')")
              b-icon(icon="chevron-right")
            a.button(@click="nav('next', 2)")
              b-icon(icon="chevron-double-right")
      .calendars
        .calendar(
          v-for="(month, m) in months"
          :key="m"
        )
          h4.is-size-5.has-text-centered {{ month.label | date('MMMM YYYY') }}
          table.table
            thead
              tr
                th(
                  v-for="weekday in weekdays"
                  :key="weekday"
                ) {{ weekday }}
            tbody
              tr(
                v-for="(week, w) in month.weeks"
                :key="w"
              )
                td(
                  v-for="(date, d) in week"
                  :key="d"
                  :class="dateClasses(date, month.label)"
                )
                  a(@click="toggleDate(date)") {{ date | date('D') }}
</template>

<script>
import _ from 'lodash';
import moment from 'moment';

export default {
  name: 'event-date-select',
  $_veeValidate: {
    value() {
      return this.dates;
    },
  },
  computed: {
    datesLabel() {
      const format = 'MMMM D, YYYY';
      let string = 'Multiple dates';
      if (this.dates.length === 1) {
        string = this.dates[0].format(format);
      } else if (this.dates.length === 2) {
        string = [
          this.dates[0].format(format),
          this.dates[1].format(format),
        ].join(' & ');
      } else if (this.dates.length > 2 && this.selectedWeekdays.length === 1 && this.selectedMonths.length <= 2) {
        const months = this.selectedMonths.join(' and ');
        string = `${this.selectedWeekdays[0]}s in ${months}`;
      }
      return string;
    },
    selectedMonths() {
      return _.uniq(_.map(this.dates, d => d.format('MMMM')));
    },
    selectedWeekdays() {
      return _.uniq(_.map(this.dates, d => d.format('dddd')));
    },
  },
  created() {
    const values = _.compact(this.value);
    if (values.length > 0) {
      let mode = 'date';
      const parsedDates = _.map(values, val => moment(val, 'YYYY-MM-DD'));
      const minDate = _.min(parsedDates);
      this.start = minDate.clone();
      if (values.length === 2) {
        mode = 'range';
        const maxDate = _.max(parsedDates);
        this.setRange('from', minDate);
        this.setRange('to', maxDate);
      } else if (values.length > 2) {
        mode = 'dates';
      }
      this.mode = mode;
      this.dates = parsedDates;
      if (mode === 'date') {
        this.date = this.dates[0];
      }
    } else {
      this.start = moment();
    }
    this.init();
  },
  data() {
    return {
      date: null,
      dates: [],
      mode: 'date',
      months: [],
      range: {
        from: null,
        to: null,
      },
      start: null,
      weekdays: [
        'Su',
        'M',
        'Tu',
        'W',
        'Th',
        'F',
        'Sa',
      ],
    };
  },
  methods: {
    dateClasses(date, month) {
      const classes = {
        'is-outside-month': !date.isSame(month, 'month'),
      };
      switch (this.mode) {
        case 'date':
          if (this.date) {
            classes['is-selected'] = this.date.isSame(date, 'day');
          }
          break;
        case 'range':
          classes['is-in-range'] = false;
          classes['is-range-limit'] = date.isSame(this.range.from) || date.isSame(this.range.to);
          if (this.range.from !== null && this.range.to !== null) {
            classes['is-in-range'] = date.isBetween(this.range.from, this.range.to);
            classes['is-first'] = date.isSame(this.range.from);
            classes['is-last'] = date.isSame(this.range.to);
          }
          break;
        case 'dates':
          classes['is-selected'] = _.filter(this.dates, d => d.isSame(date, 'day')).length > 0;
          break;
        default:
      }
      return classes;
    },
    init() {
      const second = this.start.clone().startOf('month').add(1, 'month');
      this.months = [
        this.setupMonth(this.start),
        this.setupMonth(second),
      ];
    },
    nav(direction, factor) {
      if (factor !== undefined && factor > 1) this.reset();
      const months = (factor === undefined) ? 1 : 1 * factor;
      switch (direction) {
        case 'prev':
          this.start.subtract(months, 'months');
          break;
        case 'next':
          this.start.add(months, 'months');
          break;
        default:
      }
      this.init();
    },
    reset() {
      this.date = null;
      this.dates = [];
      this.range = {
        to: null,
        from: null,
      };
      this.triggerInput(this.dates);
    },
    setRange(key, value) {
      this.range[key] = value === null ? value : value.clone();
    },
    setupMonth(target) {
      const targetMonth = target.clone().startOf('month');
      const from = targetMonth.clone().startOf('week');
      const to = targetMonth.clone().endOf('month').endOf('week');
      const month = {
        label: targetMonth,
        weeks: [],
      };
      let i = 0;
      let week = from.week();
      while (from.isBefore(to)) {
        const thisWeek = from.week();
        if (week === thisWeek && month.weeks.length > 0) {
          month.weeks[i].push(from.clone());
        } else {
          if (month.weeks.length > 0) i += 1;
          month.weeks.push([from.clone()]);
          week = thisWeek;
        }
        from.add(1, 'days');
      }
      return month;
    },
    toggleDate(date) {
      switch (this.mode) {
        case 'date': {
          if (this.date && this.date.isSame(date, 'day')) {
            this.date = null;
          } else {
            this.date = date.clone();
            this.$refs.dates.toggle();
          }
          this.triggerInput([this.date]);
          break;
        }
        case 'range': {
          if (this.range.from === null) {
            this.setRange('from', date);
          } else if (this.range.to === null) {
            if (date.isBefore(this.range.from)) {
              this.setRange('from', date);
            } else {
              this.setRange('to', date);
              this.$refs.dates.toggle();
            }
          } else {
            this.setRange('to', null);
            this.setRange('from', date);
          }
          this.triggerInput([this.range.to, this.range.from]);
          break;
        }
        case 'dates': {
          const index = _.findIndex(this.dates, d => d.isSame(date, 'day'));
          if (index >= 0) {
            this.dates.splice(index, 1);
          } else {
            this.dates.push(date.clone());
          }
          this.triggerInput(this.dates);
          break;
        }
        default:
      }
    },
    triggerInput(dates) {
      const formatted = _.map(dates, (date) => {
        if (date) {
          return date.format('YYYY-MM-DD');
        }
        return null;
      });
      this.$emit('input', _.compact(formatted) || []);
    },
  },
  props: {
    value: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  // watch: {
  //   mode: 'reset',
  // },
};
</script>

<style lang="scss">
.event-date-select {
  .dropdown-item:focus {
    outline: none;
  }
  .button {
    font-weight: 400 !important;
    font-family: 'system-ui';
    .date-label {
      padding-left: 0.35rem;
      .date-range-label {
        display: flex;
        align-items: center;
        .separator {
          padding: 0 0.5rem;
          .icon {
            position: relative;
            top: 3px;
          }
        }
      }
    }
  }
  .calendar-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }
  .calendars {
    display: flex;
    .calendar + .calendar {
      margin-left: 1.5rem;
    }
    table.table {
      thead {
        tr {
          th {
            border: none;
            font-weight: 500;
            color: $grey-light;
            font-style: normal;
          }
        }
      }
      tbody {
        tr {
          td {
            text-align: center;
            border: none;
            border-radius: 4px;
            padding: 0.5em 0.65em;
            a {
              display: block;
              color: $grey-dark;
              padding: 0.15rem;
            }
            &.is-outside-month {
              visibility: hidden;
            }
            &.is-range-limit, &.is-selected {
              background-color: $theme-primary;
              a {
                color: $white;
              }
            }
            &.is-in-range {
              background-color: $white-ter;
              &:not(.is-first):not(.is-last) {
                border-radius: 0;
              }
            }
            &.is-first:not(.is-last) {
              border-top-right-radius: 0;
              border-bottom-right-radius: 0;
            }
            &.is-last:not(.is-first) {
              border-top-left-radius: 0;
              border-bottom-left-radius: 0;
            }
          }
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/form/EventDateSelect.vue